import React, { useState, useEffect } from 'react';
import { Typography, Spin, Pagination } from 'antd';
import { Document } from '@carbon/icons-react';
import dayjs from 'dayjs';
import legacyService from 'services/legacy.service';
import { DATE_TIME_FORMAT_SENTENCE, TIMEZONE_EST } from 'components/AriaDatePicker/AriaDatePicker';

const { Text } = Typography;

const ITEMS_PER_PAGE = 4;

export default function PreviousSubmissions({ applicationId, itemId, isOpen }) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data =
        itemId !== undefined
          ? await legacyService.getChecklistItemHistory(applicationId, itemId)
          : await legacyService.getAdditionalItemsHistory(applicationId);
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemId]);

  if (!isOpen) {
    return null;
  }

  if (loading) {
    return (
      <div className="py-6 px-6 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 text-gray-500">
          <Spin size="small" />
          <Text className="text-sm">Loading previous submissions...</Text>
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return null;
  }

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedHistory = history.slice(startIndex, endIndex);
  const showPagination = history.length > ITEMS_PER_PAGE;

  return (
    <div className="pt-6 pb-6 px-6 border-t border-gray-200 bg-gray-100">
      <Text className="text-gray-500 font-medium block mb-4">
        {history.length} Previous submission{history.length !== 1 && 's'}
      </Text>

      <div className="divide-y divide-solid divide-gray-300">
        {paginatedHistory.map((submission, index) => (
          <div key={index} className="flex items-start gap-3 py-3 pt-4 first:pt-0">
            <Document size={20} className="text-gray-400 flex-shrink-0 mt-[-2px]" />

            <div className="flex-1 min-w-0">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 mb-1">
                <Text className="text-sm text-gray-700 font-semibold">
                  {dayjs(submission.dateSubmitted).tz(TIMEZONE_EST).format(DATE_TIME_FORMAT_SENTENCE)}
                </Text>

                <Text className="text-sm font-medium text-gray-700">Submitted</Text>
              </div>

              <Text className="text-sm block truncate text-gray-700">{submission.document}</Text>

              {submission.note && <Text className="text-sm italic block mt-1 text-gray-600">{submission.note}</Text>}
            </div>
          </div>
        ))}
      </div>

      {showPagination && (
        <div className="mt-4 flex justify-center">
          <Pagination
            current={currentPage}
            total={history.length}
            pageSize={ITEMS_PER_PAGE}
            onChange={setCurrentPage}
            showSizeChanger={false}
            size="small"
          />
        </div>
      )}
    </div>
  );
}
